import React, { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import Input from "../Components/Input";
import cn from "classnames";
import useDateSelections from "../hooks/useDateSelections";
import useApi from "../Api/Api";

const EventFilter = ({
  eventIds,
  setEventIds,
  filterData,
  eventFilterDate,
  setEventFilterDate: _setEventFilterDate,
}) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [eventsToRender, setEventsToRender] = useState([]);
  const [events, setEvents] = useState([]);
  const [filterBy, setFilterBy] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const api = useApi();
  const [chosenAttendance, setChosenAttendance] = useState("");
  const [chosenEventType, setChosenEventType] = useState("");
  const numberOfEvents = Object.keys(eventIds).length;
  const [perCap, setPerCap] = useState({ max: "", min: "" });

  const dropDownRef = useRef();

  const setEventFilterDate = (data) => {
    _setEventFilterDate(data);
  };

  const today = DateTime.now();

  const attendanceOptions = [
    { label: "0 - 100", value: "100" },
    { label: "100 - 500", value: "500" },
    { label: "500 - 1,000", value: "1000" },
    { label: "1,000 - 5,000", value: "5000" },
    { label: "5,000 - 10,000", value: "10000" },
    { label: "10,000 - 50,000", value: "50000" },
    { label: "50,000 - 100,000", value: "100000" },
    { label: "100,000 +", value: "max" },
    { label: "Custom", value: "custom" },
  ];

  const [customAttendance, setCustomAttendance] = useState({
    max: "",
    min: "",
  });

  const eventTypeOptions = eventTypes.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const handleSetFilterBy = (e) => {
    setFilterBy(e.target.textContent);
  };

  const { dateOptions } = useDateSelections();

  const filterByOptions = ["Date", "Event Type", "Per Cap", "Attendance"];

  const handleCheckChange = (each, state) => {
    if (state) {
      setEventIds({ ...eventIds, [each.id]: state });
    } else {
      delete eventIds[each.id];
      setEventIds({ ...eventIds });
    }
  };

  const reset = () => {
    setFilterBy("");
    setEventFilterDate({
      date_button: "",
      start_date: "",
      end_date: "",
    });
    setChosenAttendance("");
    setChosenEventType("");
    setCustomAttendance({ min: "", max: "" });
    setPerCap({ min: "", max: "" });
    setEventIds({});
  };

  const selectAll = () => {
    const obj = {};
    eventsToRender.forEach((each) => (obj[each.id] = true));
    setEventIds(obj);
  };

  const handlePerCapSelect = ({ target: { name, value } }) => {
    setPerCap({ ...perCap, [name]: value });
  };

  const handleAttendanceSelect = (option) => {
    setChosenAttendance(option.value);
  };

  const handleEventTypeOptionsSelect = (option) => {
    setChosenEventType(option.value);
  };

  const handleShowDropDown = () => {
    setShowDropDown(!showDropDown);
  };

  const handleCustomAttendance = ({ target: { name, value } }) => {
    setCustomAttendance({ ...customAttendance, [name]: value });
  };

  const handleFilterDateSelect = (e) => {
    switch (e.target?.id || e.value) {
      case "today":
        return setEventFilterDate({
          date_button: "today",
          start_date: today.toISODate(),
          end_date: today.plus({ days: 1 }).toISODate(),
        });
      case "yesterday":
        return setEventFilterDate({
          date_button: "yesterday",
          start_date: today.minus({ days: 1 }).startOf("day").toISODate(),
          end_date: today.toISODate(),
        });
      case "this_month":
        return setEventFilterDate({
          date_button: "this_month",
          start_date: today.startOf("month").toISODate(),
          end_date: today.startOf("month").plus({ months: 1 }).toISODate(),
        });
      case "last_month":
        return setEventFilterDate({
          date_button: "last_month",
          start_date: today.startOf("month").minus({ months: 1 }).toISODate(),
          end_date: today.startOf("month").toISODate(),
        });
      case "this_year":
        return setEventFilterDate({
          date_button: "this_year",
          start_date: today.startOf("year").toISODate(),
          end_date: today.startOf("year").plus({ years: 1 }).toISODate(),
        });
      case "last_year":
        return setEventFilterDate({
          date_button: "last_year",
          start_date: today.startOf("year").minus({ years: 1 }).startOf("year").toISODate(),
          end_date: today.startOf("year").toISODate(),
        });
      case "first_quarter":
        return setEventFilterDate({
          date_button: "first_quarter",
          start_date: today.set({ month: 1, day: 1 }).startOf("day").toISODate(),
          end_date: today.set({ month: 4, day: 1 }).endOf("day").toISODate(),
        });
      case "second_quarter":
        return setEventFilterDate({
          date_button: "second_quarter",
          start_date: today.set({ month: 4, day: 1 }).startOf("day").toISODate(),
          end_date: today.set({ month: 7, day: 1 }).endOf("day").toISODate(),
        });
      case "third_quarter":
        return setEventFilterDate({
          date_button: "third_quarter",
          start_date: today.set({ month: 7, day: 1 }).startOf("day").toISODate(),
          end_date: today.set({ month: 10, day: 1 }).endOf("day").toISODate(),
        });
      case "fourth_quarter":
        return setEventFilterDate({
          date_button: "fourth_quarter",
          start_date: today.set({ month: 10, day: 1 }).startOf("day").toISODate(),
          end_date: today.startOf("year").plus({ years: 1 }).toISODate(),
        });
      case "all_time":
        return setEventFilterDate({
          date_button: "all_time",
          start_date: `1900-01-01`,
          end_date: `2100-12-31`,
        });
      case "custom":
        return setEventFilterDate({
          date_button: "custom",
          start_date: "",
          end_date: "",
        });
      default:
    }
  };

  const handleCustomStartDate = (e) => {
    eventFilterDate.date_button === "custom" &&
      setEventFilterDate({
        ...eventFilterDate,
        start_date: e.target.value,
      });
  };

  const handleCustomEndDate = (e) => {
    eventFilterDate.date_button === "custom" &&
      setEventFilterDate({
        ...eventFilterDate,
        end_date: e.target.value,
      });
  };

  useEffect(() => {
    console.log("Filters", filterBy, eventFilterDate, chosenEventType, perCap, chosenAttendance, customAttendance);
    if (filterBy === "Date" && (eventFilterDate.start_date || eventFilterDate.end_date)) {
      console.log("Date Filter", eventFilterDate);
      setEventsToRender(
        events.filter((each) => each.date > eventFilterDate.start_date && each.date < eventFilterDate.end_date)
      );
    } else if (filterBy === "Event Type" && chosenEventType) {
      console.log("Event Type Filter", chosenEventType);
      setEventsToRender(events.filter((each) => each.event_type_id === chosenEventType));
    } else if (filterBy === "Per Cap" && (perCap.min || perCap.max)) {
      console.log("Per Cap Filter", perCap);
      setEventsToRender(
        events.filter(
          (each) => each.gross_sales / each.attendance >= perCap.min && each.gross_sales / each.attendance <= perCap.max
        )
      );
    } else if (filterBy === "Attendance" && (chosenAttendance || customAttendance.min || customAttendance.max)) {
      console.log("Attendance Filter", chosenAttendance, customAttendance);
      setEventsToRender(
        events.filter((each) => each.attendance >= customAttendance.min && each.attendance <= customAttendance.max)
      );
      if (chosenAttendance === 100) {
        setEventsToRender(events.filter((each) => each.attendance <= chosenAttendance));
      } else if (chosenAttendance.startsWith("5")) {
        setEventsToRender(
          events.filter((each) => each.attendance >= chosenAttendance / 5 && each.attendance <= chosenAttendance)
        );
      } else if (chosenAttendance.startsWith("1000")) {
        setEventsToRender(
          events.filter((each) => each.attendance >= chosenAttendance / 2 && each.attendance <= chosenAttendance)
        );
      } else if (chosenAttendance === "max") {
        setEventsToRender(events.filter((each) => each.attendance >= 100000));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData, chosenEventType, perCap, customAttendance, chosenAttendance, filterBy, eventFilterDate]);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (showDropDown && dropDownRef.current && !dropDownRef.current.contains(e.target)) {
        setShowDropDown(false);
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [showDropDown]);

  useEffect(() => {
    filterData.artist_id &&
      api.get(`/events/all?artist_id=${filterData.artist_id}&sort=date`).then((r) => {
        setEventsToRender(r.data);
        setEvents(r.data);
      });
  }, [api, filterData.artist_id]);

  useEffect(() => {
    api.get(`/event_types`).then((r) => {
      setEventTypes(r.data);
    });
  }, [api]);

  const renderCustomEventDateSelector = () => {
    return (
      <div
        className={eventFilterDate.date_button === "custom" ? "flex text-xs text-black" : "flex text-xs text-gray-400"}
      >
        <div className="flex flex-col w-1/2 mr-2">
          <label htmlFor="start_date">From {eventFilterDate.date_button}</label>
          <input
            value={eventFilterDate.start_date ?? ""}
            disabled={eventFilterDate.date_button !== "custom"}
            type="date"
            id="start_date"
            className="h-10 pl-2 rounded-md border"
            onChange={handleCustomStartDate}
          ></input>
        </div>
        <div className="flex flex-col w-1/2">
          <label htmlFor="end_date">To</label>
          <input
            value={eventFilterDate.end_date ?? ""}
            disabled={eventFilterDate.date_button !== "custom"}
            type="date"
            id="end_date"
            className="h-10 pl-2 rounded-md border"
            onChange={handleCustomEndDate}
          ></input>
        </div>
      </div>
    );
  };

  return (
    <div className="w-1/2 pl-12">
      <button className="border border-gray-300 text-black min-h-9 pl-2 rounded-md" onClick={handleShowDropDown}>
        <div className="flex justify-between">
          <div className="py-2 mr-12">
            {numberOfEvents === 0 && <>No shows selected</>}
            {numberOfEvents === 1 && <>{numberOfEvents} show selected</>}
            {numberOfEvents > 1 && <>{numberOfEvents} shows selected</>}
          </div>
          <div className="bg-gray-100 p-2 rounded-md">
            Filter
            <FontAwesomeIcon className="text-xs ml-2" icon={faArrowDown}></FontAwesomeIcon>
          </div>
        </div>
      </button>
      {showDropDown && (
        <div
          ref={dropDownRef}
          className="fixed min-w-80 z-10 bg-white border shadow-xl mt-2 rounded-md p-3 text-xs text-black font-normal h-80"
        >
          <div className="flex justify-between">
            <div>FILTER BY</div>
            {(filterBy !== "" || numberOfEvents > 0) && (
              <button className="text-blue-500 italic" onClick={reset}>
                Clear all
              </button>
            )}
          </div>
          <div className="flex my-3">
            {filterByOptions.map((option, i) => (
              <button
                key={i}
                id={option}
                className={
                  filterBy === option
                    ? "bg-blue-500 text-white rounded p-1 mr-2"
                    : "border border-blue-500 text-blue-500 rounded p-1 mr-2"
                }
                onClick={handleSetFilterBy}
              >
                {option}
              </button>
            ))}
          </div>
          {filterBy === "Date" && (
            <Select
              id="date"
              className="text-xs font-light w-3/4"
              placeholder="Select date range"
              options={dateOptions}
              onChange={handleFilterDateSelect}
              value={dateOptions?.filter((each) => each.value === eventFilterDate.date_button)}
            />
          )}
          {filterBy === "Event Type" && (
            <Select
              id="event_type"
              className="text-xs font-light w-3/4"
              placeholder="Select event type"
              options={eventTypeOptions}
              onChange={handleEventTypeOptionsSelect}
            />
          )}
          {filterBy === "Per Cap" && (
            <div className="flex">
              <Input name="min" placeholder="Minimum" onChange={handlePerCapSelect} value={perCap.min} />
              <div className="mx-2"></div>
              <Input name="max" placeholder="Maximum" onChange={handlePerCapSelect} value={perCap.max} />
            </div>
          )}
          {filterBy === "Attendance" && (
            <div>
              {chosenAttendance !== "custom" && (
                <Select
                  id="attendance"
                  className="text-xs font-light w-3/4"
                  placeholder="Select attendance"
                  options={attendanceOptions}
                  onChange={handleAttendanceSelect}
                  value={attendanceOptions?.filter((each) => each.value === chosenAttendance)}
                />
              )}
              {chosenAttendance === "custom" && (
                <div className="flex">
                  <Input
                    name="min"
                    placeholder="Min Attendance"
                    onChange={handleCustomAttendance}
                    value={customAttendance.min}
                  />
                  <div className="mx-2"></div>
                  <Input
                    name="max"
                    placeholder="Max Attendance"
                    onChange={handleCustomAttendance}
                    value={customAttendance.max}
                  />
                </div>
              )}
            </div>
          )}
          <div className="mt-2">
            {eventFilterDate.date_button === "custom" && filterBy === "Date" && renderCustomEventDateSelector()}
          </div>
          <button className="italic text-blue-500" onClick={selectAll}>
            Select all
          </button>
          <div
            className={cn("mt-1 fixed overflow-y-auto overflow-x-auto font-light w-72", {
              "h-52": filterBy === "",
              "h-40":
                filterBy === "Per Cap" ||
                filterBy === "Attendance" ||
                filterBy === "Event Type" ||
                (filterBy === "Date" && eventFilterDate.date_button !== "custom"),
              "h-28": eventFilterDate.date_button === "custom" && filterBy === "Date",
            })}
          >
            {eventsToRender.map((each, i) => {
              const [year, month, day] = each?.date.split("T")[0].split("-");
              each.formattedDate = `${month}/${day}/${year}`;
              return (
                <div className="mt-1" key={i}>
                  <input
                    type="checkbox"
                    id={each.id}
                    checked={eventIds[each.id] ?? false}
                    onChange={() => handleCheckChange(each, !eventIds[each.id])}
                  />
                  <label htmlFor={each.id} className="ml-2">
                    {each.formattedDate} {each.city || each.name} {each.attendance}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventFilter;
